import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { Calendar, MapPin, Users, Clock, Search, Filter, RefreshCw } from 'lucide-react';
import { format } from 'date-fns';
import { SiteFilter } from '@/components/admin/SiteFilter';
import { useUserContext } from '@/hooks/useUserContext';

interface CheckinData {
  id: string;
  worker_id: string;
  worker_name: string;
  shift_name: string;
  work_location: string;
  check_in_date: string;
  working_with: string[];
  planned_tasks: string[];
  gps_coordinates: string | null;
  site_id: string | null;
  site_name?: string;
  created_at: string;
}

interface AdminCheckinsProps {
  siteId?: string | null;
}

const AdminCheckins = ({ siteId: propSiteId }: AdminCheckinsProps = {}) => {
  const { companyId, isLoading: contextLoading } = useUserContext();
  const [checkins, setCheckins] = useState<CheckinData[]>([]);
  const [filteredCheckins, setFilteredCheckins] = useState<CheckinData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSite, setSelectedSite] = useState(propSiteId || 'all');
  const [selectedShift, setSelectedShift] = useState('all');
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));

  useEffect(() => {
    if (propSiteId) {
      setSelectedSite(propSiteId);
    }
  }, [propSiteId]);

  useEffect(() => {
    if (!contextLoading && companyId) {
      fetchCheckins();
    }
  }, [selectedDate, contextLoading, companyId]);

  useEffect(() => {
    filterCheckins();
  }, [checkins, searchQuery, selectedSite, selectedShift]);

  const fetchCheckins = async () => {
    if (!companyId) return;
    
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('shift_checkins')
        .select(`
          *,
          worker:worker_profiles!worker_id(display_name, full_name, company_id),
          site:company_locations!site_id(location_name)
        `)
        .eq('check_in_date', selectedDate)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Filter by company_id from worker profile
      const transformedData: CheckinData[] = (data || [])
        .filter((checkin: any) => checkin.worker?.company_id === companyId)
        .map((checkin: any) => ({
          id: checkin.id,
          worker_id: checkin.worker_id,
          worker_name: checkin.worker?.display_name || checkin.worker?.full_name || 'Unknown',
          shift_name: checkin.shift_name,
          work_location: checkin.work_location,
          check_in_date: checkin.check_in_date,
          working_with: checkin.working_with || [],
          planned_tasks: checkin.planned_tasks || [],
          gps_coordinates: checkin.gps_coordinates,
          site_id: checkin.site_id,
          site_name: checkin.site?.location_name,
          created_at: checkin.created_at,
        }));

      setCheckins(transformedData);
    } catch (error) {
      console.error('Error fetching checkins:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filterCheckins = () => {
    let filtered = [...checkins];

    if (searchQuery) {
      filtered = filtered.filter(c => 
        c.worker_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.work_location.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (selectedSite !== 'all') {
      filtered = filtered.filter(c => c.site_id === selectedSite);
    }

    if (selectedShift !== 'all') {
      filtered = filtered.filter(c => c.shift_name === selectedShift);
    }

    setFilteredCheckins(filtered);
  };

  // Get unique shifts for filter
  const uniqueShifts = [...new Set(checkins.map(c => c.shift_name))];

  // Stats
  const totalCheckins = filteredCheckins.length;
  const uniqueLocations = new Set(filteredCheckins.map(c => c.work_location)).size;
  const avgCoworkers = filteredCheckins.length > 0 
    ? Math.round(filteredCheckins.reduce((sum, c) => sum + c.working_with.length, 0) / filteredCheckins.length)
    : 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Check-in Data</h2>
          <p className="text-muted-foreground">Monitor daily worker check-ins and team distributions</p>
        </div>
        <Button onClick={fetchCheckins} variant="outline">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Users className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{totalCheckins}</p>
                <p className="text-xs text-muted-foreground">Total Check-ins</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-success/10">
                <MapPin className="h-5 w-5 text-success" />
              </div>
              <div>
                <p className="text-2xl font-bold">{uniqueLocations}</p>
                <p className="text-xs text-muted-foreground">Active Locations</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-warning/10">
                <Users className="h-5 w-5 text-warning" />
              </div>
              <div>
                <p className="text-2xl font-bold">{avgCoworkers}</p>
                <p className="text-xs text-muted-foreground">Avg. Team Size</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-secondary">
                <Clock className="h-5 w-5 text-secondary-foreground" />
              </div>
              <div>
                <p className="text-2xl font-bold">{uniqueShifts.length}</p>
                <p className="text-xs text-muted-foreground">Active Shifts</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <Input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-40"
              />
            </div>
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search worker or location..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            {!propSiteId && <SiteFilter value={selectedSite} onChange={setSelectedSite} />}
            <Select value={selectedShift} onValueChange={setSelectedShift}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Shift" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Shifts</SelectItem>
                {uniqueShifts.map(shift => (
                  <SelectItem key={shift} value={shift}>{shift}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Check-ins Table */}
      <Card>
        <CardHeader>
          <CardTitle>Check-in Records</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : filteredCheckins.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No check-ins found for {format(new Date(selectedDate), 'dd MMM yyyy')}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Worker</TableHead>
                  <TableHead>Shift</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Site</TableHead>
                  <TableHead>Team Size</TableHead>
                  <TableHead>Planned Tasks</TableHead>
                  <TableHead>Time</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCheckins.map((checkin) => (
                  <TableRow key={checkin.id}>
                    <TableCell className="font-medium">{checkin.worker_name}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{checkin.shift_name}</Badge>
                    </TableCell>
                    <TableCell>{checkin.work_location}</TableCell>
                    <TableCell>{checkin.site_name || '-'}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Users className="h-3 w-3 text-muted-foreground" />
                        <span>{checkin.working_with.length + 1}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {checkin.planned_tasks.slice(0, 2).map((task, idx) => (
                          <Badge key={idx} variant="secondary" className="text-xs">
                            {task}
                          </Badge>
                        ))}
                        {checkin.planned_tasks.length > 2 && (
                          <Badge variant="secondary" className="text-xs">
                            +{checkin.planned_tasks.length - 2}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {format(new Date(checkin.created_at), 'HH:mm')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminCheckins;
